// CreateRole.js
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Modal, Input, Select } from "antd";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next"
import {
  createRoleThunk,
  getAllRolesThunk,
  getPermissionsThunk,
} from "../../../Store/Slices/Common/RBAC/rbacThunks";
import { Option } from "antd/es/mentions";


const CreateRole = ({ onClose, }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, error, permissions } = useSelector(
    (state) => state.admin.rbac
  );
 useEffect(() => {
    dispatch(getAllRolesThunk());
  }, [dispatch]);
  // Local States
  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [department, setDepartment] = useState("");
  const [errors, setErrors] = useState({ roleName: "", description: "" });
  const { t } = useTranslation("admUserProfile")
  // Handle create role click
  const handleCreate = async () => {

    const trimmedName = roleName.trim();
    const trimmedDesc = description.trim();

    const newErrors = { roleName: "", description: "" };
    let hasError = false;

    if (!trimmedName) {
      newErrors.roleName = t("Role name is required.");
      hasError = true;
    }
    if (!trimmedDesc) {
      newErrors.description = t("Description is required.");
      hasError = true;
    }
    if (hasError) {
      setErrors(newErrors);
      return;
    }
    // Clear previous errors
    setErrors({ roleName: "", description: "" });

    // Update department: if department (lowercase) is "otherstaff", send "otherStaff", otherwise lowercase
    const departmentPayload =
      department.toLowerCase() === "staff" ? "staff" : department.toLowerCase();

    try {
      await dispatch(
        createRoleThunk({
          name: trimmedName,
          description: trimmedDesc,
          department: departmentPayload,
          permission: [],
        })
      ).unwrap();

      toast.success(t("Role created successfully!"));
      setRoleName("");
      setDescription("");
      onClose(); // closes the CreateRole UI

      // Navigate to Manage Roles page in "edit mode"
      navigate("/users/manage-roles", {
        state: {
          department,
          role: trimmedName,
          editMode: true,
        },
      });
    } catch (err) {
      console.error("Failed to create role:", err);
      toast.error(t("Failed to create role"));
    }
  };

  const { roles } = useSelector((state) => state.admin.rbac);
 
  const [options, setOptions] = useState(roles?.map(i => i.department));

  // This function is called whenever the user types in the input field
  const handleSearch = (inputValue) => {
    // Check if the typed value is not already in the options list
    if (inputValue && !options.includes(inputValue)) {
      // Add the new value to the beginning of the options list
      setOptions([inputValue, ...options]);
    }
  };
  return (
    <>
      {/* Main Container with sticky footer */}
      <motion.div
        className="flex flex-col h-full bg-white"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        transition={{ duration: 0.3 }}
      >
        {/* 1) Top: Role Details */}
        <div className="p-2 px-4 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t("Department")}
                </label>
                <Select
                  placeholder="Enter Dep"
                  value={department} // Use 'value' instead of 'onChange' directly to control the component
                  onChange={(e) => setDepartment(e)}
                  style={{ width: '100%' }}
                  showSearch // Allows the user to type and search
                  allowClear // Adds a clear button
                  onSearch={handleSearch} // Handles custom input
                  // This filter is important to show only relevant options as the user types
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {options.map((departmentName) => (
                    <Option key={departmentName} value={departmentName}>
                      {departmentName}
                    </Option>
                  ))}
                </Select>
              </div>
              {/* Role Name */}
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t("Role Name")}
                </label>
                <input
                  type="text"
                  placeholder={t("Enter role")}
                  value={roleName}
                  onChange={(e) => {
                    setRoleName(e.target.value);
                    if (errors.roleName) {
                      setErrors((prev) => ({ ...prev, roleName: "" }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-pink-500 focus:outline-none ${errors.roleName ? "border-red-500" : "border-gray-300"
                    }`}
                />
                {errors.roleName && (
                  <p className="text-red-500 text-xs mt-1">{errors.roleName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t("Description")}
                </label>
                <textarea
                  placeholder={t("Type here...")}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (errors.description) {
                      setErrors((prev) => ({ ...prev, description: "" }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded h-32 resize-vertical focus:ring-2 focus:ring-pink-500 focus:outline-none ${errors.description ? "border-red-500" : "border-gray-300"
                    }`}
                />
                {errors.description && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.description}
                  </p>
                )}
              </div>
            </div>

            {/* Right Column: Description */}

          </div>
          <button
            onClick={handleCreate}
            disabled={loading}
            className={`px-4 w-40 py-1 text-white rounded bg-gradient-to-r from-pink-600 to-pink-800 hover:opacity-90 ${loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
          >
            {loading ? t("Creating...") : t("Create Role")}
          </button>
        </div>




      </motion.div>


    </>
  );
};

export default CreateRole;
